const DEFAULT_COOKIE_SAME_SITE = 'Lax';

let sharedCookieDomainCache: string | null = null;
let hasResolvedSharedCookieDomain = false;

export interface WriteCookieOptions {
  maxAge?: number;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookies = document.cookie ? document.cookie.split('; ') : [];

  for (const cookie of cookies) {
    const [cookieName, ...rest] = cookie.split('=');

    if (cookieName === encodeURIComponent(name)) {
      return decodeURIComponent(rest.join('='));
    }
  }

  return null;
}

function getSharedCookieDomain(): string | undefined {
  if (hasResolvedSharedCookieDomain) {
    return sharedCookieDomainCache || undefined;
  }

  if (typeof window === 'undefined') {
    return undefined;
  }

  const hostname = window.location.hostname;

  if (!hostname || hostname === 'localhost' || hostname.includes(':')) {
    return undefined;
  }

  const hostParts = hostname.split('.');

  if (hostParts.length < 2) {
    return undefined;
  }

  const testCookieName = '__domain_test__';
  const testCookieValue = '1';

  for (let index = hostParts.length - 2; index >= 0; index -= 1) {
    const candidateDomain = hostParts.slice(index).join('.');

    document.cookie = `${encodeURIComponent(
      testCookieName
    )}=${testCookieValue}; Path=/; Domain=${candidateDomain}; SameSite=${DEFAULT_COOKIE_SAME_SITE}`;

    if (readCookie(testCookieName) === testCookieValue) {
      document.cookie = `${encodeURIComponent(testCookieName)}=; Path=/; Domain=${candidateDomain}; Max-Age=0; SameSite=${DEFAULT_COOKIE_SAME_SITE}`;
      sharedCookieDomainCache = candidateDomain;
      hasResolvedSharedCookieDomain = true;
      return candidateDomain;
    }
  }

  sharedCookieDomainCache = null;
  hasResolvedSharedCookieDomain = true;

  return undefined;
}

export function writeCookie(name: string, value: string, options?: WriteCookieOptions): void {
  if (typeof document === 'undefined') {
    return;
  }

  const sameSite = options?.sameSite ?? DEFAULT_COOKIE_SAME_SITE;
  const sharedDomain = getSharedCookieDomain();
  const isSecureContext = typeof window !== 'undefined' && window.location.protocol === 'https:';

  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Path=/; SameSite=${sameSite}`;

  if (options?.maxAge !== undefined) {
    cookie += `; Max-Age=${options.maxAge}`;
  }

  if (sharedDomain) {
    cookie += `; Domain=${sharedDomain}`;
  }

  if (isSecureContext) {
    cookie += '; Secure';
  }

  document.cookie = cookie;
}
